/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This file contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this file can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

import { useEffect, useSyncExternalStore } from 'react';
import {
  addBridgeMessageListener,
  TeammateAppEventPayload,
  type AppStateEventPayload,
  type BridgeEventSubscriber,
} from '@/utils/bridge';
import { DEFAULT_LOCALE } from '@/utils/locale';
import { ThemeType } from '@/utils/theme';

type NOOP = () => void;

let appStateListeners: NOOP[] = [];
let unsubscribeToBridgeEvent: NOOP;

let bridgeAppState: AppStateEventPayload = {
  locale: DEFAULT_LOCALE,
  navbarCollapsed: false,
  theme: ThemeType.Light,
};

export function setBridgeAppState(newState: Partial<AppStateEventPayload>) {
  bridgeAppState = { ...bridgeAppState, ...newState };
}
function getLocaleFromBridgeAppState() {
  return bridgeAppState.locale;
}
function getNavbarCollapsedFromBridgeAppState() {
  return bridgeAppState.navbarCollapsed;
}
function getThemeFromBridgeAppState() {
  return bridgeAppState.theme;
}

export function notifyAppStateChanged() {
  appStateListeners.forEach((listener) => {
    listener();
  });
}

const bridgeEventHandler: BridgeEventSubscriber = ({ type, payload }) => {
  if (type === 'app:state-change') {
    setBridgeAppState(payload);
    notifyAppStateChanged();
  }
};

function subscribeToBridgeState(listener: () => void) {
  // If this is the first listener, subscribe to the bridge event
  if (appStateListeners.length === 0) {
    unsubscribeToBridgeEvent = addBridgeMessageListener(bridgeEventHandler);
  }

  appStateListeners.push(listener);

  return () => {
    appStateListeners = appStateListeners.filter((l) => l !== listener);
    // If this is the last listener, unsubscribe from the bridge event
    if (appStateListeners.length === 0) {
      unsubscribeToBridgeEvent();
    }
  };
}

export function useCurrentLocale() {
  return useSyncExternalStore(subscribeToBridgeState, getLocaleFromBridgeAppState);
}

export function useNavbarCollapsed() {
  return useSyncExternalStore(subscribeToBridgeState, getNavbarCollapsedFromBridgeAppState);
}

export function useTheme() {
  return useSyncExternalStore(subscribeToBridgeState, getThemeFromBridgeAppState);
}

export function useTeammateAppEvent(
  identifier: string,
  callback: (data: TeammateAppEventPayload['data']) => void,
) {
  useEffect(() => {
    const unsubscribeToBridgeEvent = addBridgeMessageListener(({ type, payload }) => {
      if (type === 'teammate-app:event' && payload.identifier === identifier) {
        callback(payload.data);
      }
    });

    return () => {
      unsubscribeToBridgeEvent();
    };
  }, [identifier, callback]);
}
